import { useState } from 'react'
import { ArrowLeft, Search, X, Users, UserPlus, Pencil } from 'lucide-react'
import { useMyFollows, useMyFollowers, useFollowPlayer, useUnfollowPlayer } from '../hooks/useFollows'
import { usePlayers } from '../hooks/usePlayers'
import { useAuth } from '../contexts/AuthContext'
import { hexToRgba, getInitials } from '../utils/colors'
import { hasHandicapIndex, formatHandicapIndex } from '../utils/handicaps'
import AddPlayerModal from './AddPlayerModal'
import Spinner from './Spinner'

export default function FollowsList({ onBack, onViewProfile }) {
  const { user } = useAuth()
  const { data: follows, isLoading: followsLoading } = useMyFollows()
  const { data: followers, isLoading: followersLoading } = useMyFollowers()
  const { data: allPlayers, isLoading: playersLoading } = usePlayers()
  const followMutation = useFollowPlayer()
  const unfollowMutation = useUnfollowPlayer()

  const [searchText, setSearchText] = useState('')
  const [tab, setTab] = useState('following')
  const [showCreateModal, setShowCreateModal] = useState(false)

  const isLoading = followsLoading || followersLoading || playersLoading

  if (isLoading) {
    return <Spinner message="Loading..." />
  }

  const followedIds = new Set(follows?.map((p) => p.id) || [])

  // Filter players by search text (name or email)
  // Include registered users + manual players created by current user
  const searchResults = searchText.length >= 1 && allPlayers
    ? allPlayers.filter((p) => {
        if (!p.user_id && p.created_by !== user?.id) return false
        const q = searchText.toLowerCase()
        return (
          p.first_name?.toLowerCase().startsWith(q) ||
          p.last_name?.toLowerCase().startsWith(q) ||
          p.email?.toLowerCase().startsWith(q) ||
          p.display_name?.toLowerCase().split(/\s+/).some(w => w.startsWith(q))
        )
      })
    : []

  const followersCount = followers?.length || 0
  const followingCount = follows?.length || 0


  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem',
      }}>
        <button
          onClick={onBack}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--bg-tertiary)',
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: '700' }}>Friends</h1>
      </div>

      {/* Toggle badges */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginBottom: '1.5rem' }}>
        <button onClick={() => setTab('following')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: tab === 'following' ? 'var(--accent-primary)' : 'var(--bg-secondary)',
            border: tab === 'following' ? 'none' : '1px solid var(--border-color)',
            boxShadow: tab === 'following' ? 'none' : 'var(--card-shadow, none)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem',
            fontWeight: '700',
            color: tab === 'following' ? '#fff' : 'var(--text-primary)',
            transition: 'all 0.15s',
          }}>
            {followingCount}
          </div>
          <span style={{ fontSize: '0.6875rem', color: tab === 'following' ? 'var(--accent-primary)' : 'var(--text-muted)', fontWeight: tab === 'following' ? '700' : '500', textAlign: 'center', lineHeight: 1.3 }}>
            Following
          </span>
        </button>
        <button onClick={() => setTab('followers')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: tab === 'followers' ? 'var(--accent-primary)' : 'var(--bg-secondary)',
            border: tab === 'followers' ? 'none' : '1px solid var(--border-color)',
            boxShadow: tab === 'followers' ? 'none' : 'var(--card-shadow, none)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem',
            fontWeight: '700',
            color: tab === 'followers' ? '#fff' : 'var(--text-primary)',
            transition: 'all 0.15s',
          }}>
            {followersCount}
          </div>
          <span style={{ fontSize: '0.6875rem', color: tab === 'followers' ? 'var(--accent-primary)' : 'var(--text-muted)', fontWeight: tab === 'followers' ? '700' : '500', textAlign: 'center', lineHeight: 1.3 }}>
            Followers
          </span>
        </button>
      </div>

      {/* Add Player Manually button */}
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '0.75rem',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
        marginBottom: '1rem',
      }}>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
            width: '100%',
            padding: '0.75rem',
            fontSize: '0.8125rem',
            fontWeight: '600',
            color: 'var(--text-secondary)',
          }}
        >
          <UserPlus size={16} />
          Add Player Manually
        </button>
      </div>

      {showCreateModal && (
        <AddPlayerModal onClose={() => setShowCreateModal(false)} startManual />
      )}

      {/* Search bar */}
      <div style={{ position: 'relative', marginBottom: '1rem' }}>
        <Search
          size={18}
          style={{
            position: 'absolute',
            left: '0.875rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-muted)',
            pointerEvents: 'none',
          }}
        />
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search players by name or email..."
          style={{
            width: '100%',
            height: '44px',
            paddingLeft: '2.75rem',
            paddingRight: searchText ? '2.5rem' : '0.875rem',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            color: 'var(--text-primary)',
          }}
        />
        {searchText && (
          <button
            onClick={() => setSearchText('')}
            style={{
              position: 'absolute',
              right: '0.625rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Search results */}
          {searchText.length >= 1 && (
            <div style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '0.75rem',
              border: '1px solid var(--border-color)',
              padding: '0.75rem',
              marginBottom: '1rem',
            }}>
              <h3 style={sectionHeaderStyle}>
                Search Results ({searchResults.length})
              </h3>
              {searchResults.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', padding: '0.5rem 0' }}>
                  No players found
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {searchResults.map((player) => (
                    <PlayerRow
                      key={player.id}
                      player={player}
                      isFollowed={followedIds.has(player.id)}
                      isManual={!player.user_id && player.created_by === user?.id}
                      onTap={() => onViewProfile(player.id)}
                      onFollow={() => followMutation.mutate(player.id)}
                      onUnfollow={() => unfollowMutation.mutate(player.id)}
                      isPending={followMutation.isPending || unfollowMutation.isPending}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Following / Followers list */}
          {searchText.length < 1 && tab === 'following' && (
            <>
              {!follows || follows.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem 1rem',
                  color: 'var(--text-muted)',
                }}>
                  <Users size={40} style={{ marginBottom: '0.75rem', opacity: 0.5 }} />
                  <p style={{ fontSize: '0.9375rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                    Not following anyone yet
                  </p>
                  <p style={{ fontSize: '0.8125rem' }}>
                    Search for players above to find and follow them. Following a player lets you see their matches and quickly add them to yours.
                  </p>
                </div>
              ) : (
                <div style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: '0.75rem',
                  border: '1px solid var(--border-color)',
                  padding: '0.75rem',
                }}>
                  <h3 style={sectionHeaderStyle}>
                    Following ({follows.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {follows.map((player) => (
                      <PlayerRow
                        key={player.id}
                        player={player}
                        isFollowed={true}
                        isManual={!player.user_id && player.created_by === user?.id}
                        onTap={() => onViewProfile(player.id)}
                        onFollow={() => {}}
                        onUnfollow={() => unfollowMutation.mutate(player.id)}
                        isPending={unfollowMutation.isPending}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {searchText.length < 1 && tab === 'followers' && (
            <>
              {!followers || followers.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem 1rem',
                  color: 'var(--text-muted)',
                }}>
                  <Users size={40} style={{ marginBottom: '0.75rem', opacity: 0.5 }} />
                  <p style={{ fontSize: '0.9375rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                    No followers yet
                  </p>
                  <p style={{ fontSize: '0.8125rem' }}>
                    When other players follow you, they'll appear here.
                  </p>
                </div>
              ) : (
                <div style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: '0.75rem',
                  border: '1px solid var(--border-color)',
                  padding: '0.75rem',
                }}>
                  <h3 style={sectionHeaderStyle}>
                    Followers ({followers.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {followers.map((player) => (
                      <PlayerRow
                        key={player.id}
                        player={player}
                        isFollowed={followedIds.has(player.id)}
                        onTap={() => onViewProfile(player.id)}
                        onFollow={() => followMutation.mutate(player.id)}
                        onUnfollow={() => unfollowMutation.mutate(player.id)}
                        isPending={followMutation.isPending || unfollowMutation.isPending}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
    </div>
  )
}

function PlayerRow({ player, isFollowed, isManual, onTap, onFollow, onUnfollow, isPending }) {
  const initials = getInitials(player) || player.display_name?.charAt(0)?.toUpperCase() || '?'
  const color = player.avatar_color || '#0ea47a'

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.625rem',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      width: '100%',
    }}>
      <button
        onClick={onTap}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
          flex: 1,
          minWidth: 0,
          textAlign: 'left',
        }}
      >
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          backgroundColor: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: '700',
          fontSize: '0.75rem',
          color: '#fff',
          flexShrink: 0,
        }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {isManual && (
            <div style={{
              fontSize: '0.5625rem',
              fontWeight: '600',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '0.125rem',
            }}>
              Manually Created
            </div>
          )}
          <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
            {player.first_name || player.last_name
              ? `${player.first_name || ''} ${player.last_name || ''}`.trim()
              : player.display_name}
          </div>
          {player.display_name && (player.first_name || player.last_name) && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {player.display_name}
            </div>
          )}
          {hasHandicapIndex(player.handicap_index) && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              HCP: {formatHandicapIndex(player.handicap_index)}
            </div>
          )}
        </div>
      </button>
      {isManual ? (
        <button
          onClick={onTap}
          style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            padding: '0.3rem 0.75rem',
            borderRadius: '2rem',
            flexShrink: 0,
            color: 'var(--text-secondary)',
            border: '1.5px solid var(--border-color)',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
          }}
        >
          <Pencil size={12} />
          Edit
        </button>
      ) : (
        <button
          onClick={isFollowed ? onUnfollow : onFollow}
          disabled={isPending}
          style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            padding: '0.3rem 0.75rem',
            borderRadius: '2rem',
            flexShrink: 0,
            cursor: isPending ? 'wait' : 'pointer',
            transition: 'all 0.15s',
            ...(isFollowed
              ? {
                  color: 'var(--text-secondary)',
                  border: '1.5px solid var(--border-color)',
                  backgroundColor: 'transparent',
                }
              : {
                  color: '#fff',
                  border: '1.5px solid var(--accent-primary)',
                  backgroundColor: 'var(--accent-primary)',
                }),
            opacity: isPending ? 0.6 : 1,
          }}
        >
          {isFollowed ? 'Following' : 'Follow'}
        </button>
      )}
    </div>
  )
}

const sectionHeaderStyle = {
  fontSize: '0.75rem',
  fontWeight: '600',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '0.5rem',
}
